import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, Calendar } from 'lucide-react';

const Newsletter = () => {
  const fallbackHeroThumbnails = [
    '/newsletters/thumbnail/laadli.png',
    '/newsletters/thumbnail/WhatsApp Image 2026-02-22 at 12.39.20 PM.jpeg',
    '/newsletters/thumbnail/December.png',
    '/newsletters/thumbnail/November.png',
    '/newsletters/thumbnail/October.png',
    '/newsletters/thumbnail/September.jpeg',
    '/newsletters/thumbnail/August.png',
    '/newsletters/thumbnail/July.png',
    '/newsletters/thumbnail/June.png',
    '/newsletters/thumbnail/May.png',
    '/newsletters/thumbnail/April.png'
  ];
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const heroThumbnails = (newsletters.length > 0
    ? newsletters.map((newsletter) => newsletter.thumbnail).filter(Boolean)
    : fallbackHeroThumbnails
  ).slice(0, 12);

  useEffect(() => {
    const fetchNewsletters = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/newsletters.json');
        if (!response.ok) {
          throw new Error('Failed to fetch newsletters');
        }
        const data = await response.json();
        setNewsletters(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching newsletters:', err);
        setError('Failed to load newsletters. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletters();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-4 gap-0.5 md:grid-cols-4 md:grid-rows-3">
          {heroThumbnails.map((image, index) => (
            <div key={index} className="relative overflow-hidden">
              <img
                src={image}
                alt="Newsletter cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-6">
            Our Newsletters
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Stay updated with our monthly newsletters showcasing our rescue stories, impact, and upcoming events.
          </p>
        </div>
      </section>

      {/* Newsletter Grid */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">Loading newsletters...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          ) : newsletters.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">No newsletters available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {newsletters.map((newsletter) => (
                <Card key={newsletter.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="relative h-48 bg-gradient-to-br from-forest-green to-warm-amber flex items-center justify-center">
                    <img
                      src={newsletter.thumbnail || "/newsletters/thumbnail/default.png"}
                      alt={newsletter.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-white text-sm font-medium">{newsletter.date}</span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold font-heading text-forest-green mb-3">
                      {newsletter.title}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-4">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm">{newsletter.date}</span>
                    </div>
                    <a
                      href={newsletter.file}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full bg-warm-amber hover:bg-amber-600 text-white transition-colors duration-200">
                        <Download className="w-4 h-4 mr-2" />
                        View Newsletter
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
            Never Miss an Update
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-8">
            Our newsletters bring you heartwarming rescue stories, volunteer spotlights, and ways you can help make a difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
            <p className="text-gray-600">
              Follow us on social media for real-time updates and more rescue stories!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Newsletter;
